import { PaginationControls } from "@/components/pagination-controls";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { usePagination } from "@/hooks/use-pagination";
import { useToast } from "@/hooks/use-toast";
import { ContentAlbumService } from "@/lib/api/album.service";
import { categoryService } from "@/lib/api/categories.service";
import { ContentTagService } from "@/lib/api/content.tag.service";
import { useAuth } from "@/lib/auth";
import { queryClient } from "@/lib/queryClient";
import type {
  Category,
  StudioAlbum,
  TagOption,
  TaxonomyStatus,
} from "@/lib/schema";
import { useMutation, useQuery } from "@tanstack/react-query";
import {
  Album,
  Archive,
  ArrowLeft,
  CheckCircle2,
  ListMusic,
  Loader2,
  MoreHorizontal,
  Music,
  Pencil,
  Plus,
  Search,
  Video,
} from "lucide-react";
import { useEffect, useState } from "react";
import { Link, useLocation } from "wouter";

const STATUS_CONFIG: Record<
  TaxonomyStatus,
  {
    label: string;
    variant: "default" | "secondary" | "outline" | "destructive";
  }
> = {
  active: { label: "Active", variant: "default" },
  archived: { label: "Archived", variant: "secondary" },
};

export default function AlbumManager() {
  const { user } = useAuth();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [categoryFilter, setCategoryFilter] = useState<string>("all");
  const [creatorFilter, setCreatorFilter] = useState<string>("all");

  const isAdmin =
    user?.roles.includes("super_admin") || user?.roles.includes("admin");

  useEffect(() => {
    if (user && user.roles.includes("coach")) {
      setLocation("/studio");
    }
  }, [user, setLocation]);

  const { data: albumsData, isLoading } = useQuery({
    queryKey: ["content-albums", searchQuery, statusFilter, categoryFilter],
    queryFn: () =>
      ContentAlbumService.findAll({
        search: searchQuery || undefined,
        status:
          statusFilter !== "all" ? (statusFilter as TaxonomyStatus) : undefined,
        categoryId: categoryFilter !== "all" ? categoryFilter : undefined,
      }),
  });
  const albums: StudioAlbum[] = albumsData?.items ?? [];

  const { data: categories = [] } = useQuery<Category[]>({
    queryKey: ["/content-category"],
    queryFn: () => categoryService.findAll(),
  });

  const { data: tagOptions = [] } = useQuery<TagOption[]>({
    queryKey: ["/content/tag-options"],
    queryFn: () => ContentTagService.getTagOptions(),
  });

  const { data: creators = [] } = useQuery<{ id: string; name: string }[]>({
    queryKey: ["content-albums", "creators"],
    queryFn: () => ContentAlbumService.getCreators(),
  });

  const statusMutation = useMutation({
    mutationFn: async ({
      id,
      status,
    }: {
      id: string;
      status: TaxonomyStatus;
    }) => {
      return status === "archived"
        ? ContentAlbumService.archive(id)
        : ContentAlbumService.restore(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["content-albums"] });
      toast({ title: "Status updated" });
    },
    onError: () => {
      toast({ title: "Failed to update status", variant: "destructive" });
    },
  });

  function getCategoryName(categoryId: string | null): string {
    if (!categoryId) return "Uncategorised";
    const cat = categories.find((c) => c.id === categoryId);
    return cat?.title || "Unknown";
  }

  function getContentTypeIcon(type: string) {
    switch (type) {
      case "audio":
        return <Music className="h-3.5 w-3.5 text-blue-500" />;
      case "video":
        return <Video className="h-3.5 w-3.5 text-purple-500" />;
      default:
        return null;
    }
  }

  const filtered = albums.filter(
    (album) => creatorFilter === "all" || album.createdBy === creatorFilter,
  );

  const { pageItems, ...paginationProps } = usePagination(filtered, {
    pageSize: 9,
  });

  return (
    <div className="p-6 space-y-6" data-testid="album-manager">
      <div className="flex items-center gap-4 flex-wrap">
        <Link href="/studio">
          <Button variant="ghost" size="icon" data-testid="button-back-studio">
            <ArrowLeft className="h-5 w-5" />
          </Button>
        </Link>
        <div className="flex-1 min-w-0">
          <h1
            className="font-serif text-2xl font-semibold text-foreground"
            data-testid="text-page-title"
          >
            Albums
          </h1>
          <p className="text-sm text-muted-foreground">
            Curated collections of audio and video content
          </p>
        </div>
        <Button
          onClick={() => setLocation("/studio/albums/new")}
          data-testid="button-create-album"
        >
          <Plus className="h-4 w-4 mr-2" />
          Create Album
        </Button>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[200px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            className="pl-9"
            placeholder="Search albums..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="input-search"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[140px]" data-testid="filter-status">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="archived">Archived</SelectItem>
          </SelectContent>
        </Select>
        {categories.length > 0 && (
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-category">
              <SelectValue placeholder="Filter by category" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All categories</SelectItem>
              {categories.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {creators.length > 0 && (
          <Select value={creatorFilter} onValueChange={setCreatorFilter}>
            <SelectTrigger className="w-[160px]" data-testid="filter-creator">
              <SelectValue placeholder="Filter by creator" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All creators</SelectItem>
              {creators.map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {isLoading ? (
        <Card>
          <CardContent className="flex items-center justify-center py-16">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <Card data-testid="list-albums-empty">
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <div className="p-3 rounded-full bg-primary/10 mb-4">
              <Album className="h-8 w-8 text-primary" />
            </div>
            <p className="text-muted-foreground mb-4">
              {albums.length === 0
                ? "No albums created yet"
                : "No albums match your filters"}
            </p>
            {albums.length === 0 && (
              <Button
                variant="outline"
                onClick={() => setLocation("/studio/albums/new")}
                data-testid="button-create-album-empty"
              >
                <Plus className="h-4 w-4 mr-2" />
                Create your first album
              </Button>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {pageItems.map((album) => {
              const itemCount = album.itemCount ?? 0;
              return (
                <Card
                  key={album.id}
                  className="overflow-hidden cursor-pointer hover:ring-1 hover:ring-primary/20 transition-all"
                  onClick={() => setLocation(`/studio/albums/${album.id}/edit`)}
                  data-testid={`card-album-${album.id}`}
                >
                  <div className="flex">
                    <div className="relative w-28 h-28 shrink-0 bg-gradient-to-br from-muted/60 to-muted">
                      {album.coverImage ? (
                        <img
                          src={album.coverImage}
                          alt={album.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Album className="h-8 w-8 text-muted-foreground/40" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0 p-3 flex flex-col justify-between">
                      <div className="space-y-1.5">
                        <div className="flex items-start justify-between gap-2">
                          <div className="min-w-0">
                            <h3 className="font-semibold text-foreground text-sm leading-tight truncate">
                              {album.name}
                            </h3>
                            {album.description && (
                              <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">
                                {album.description}
                              </p>
                            )}
                          </div>
                          <div
                            className="shrink-0 flex items-center gap-1"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="icon"
                                  className="h-7 w-7"
                                  data-testid={`button-actions-${album.id}`}
                                >
                                  <MoreHorizontal className="h-3.5 w-3.5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end">
                                {(isAdmin || album.createdBy === user?.id) && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      setLocation(
                                        `/studio/albums/${album.id}/edit`,
                                      )
                                    }
                                    data-testid={`action-edit-${album.id}`}
                                  >
                                    <Pencil className="h-4 w-4 mr-2" />
                                    Edit Album
                                  </DropdownMenuItem>
                                )}
                                {album.status === "active" && isAdmin && (
                                  <>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem
                                      onClick={() =>
                                        statusMutation.mutate({
                                          id: album.id,
                                          status: "archived",
                                        })
                                      }
                                      className="text-destructive focus:text-destructive"
                                      data-testid={`action-archive-${album.id}`}
                                    >
                                      <Archive className="h-4 w-4 mr-2" />
                                      Archive
                                    </DropdownMenuItem>
                                  </>
                                )}
                                {album.status === "archived" && isAdmin && (
                                  <DropdownMenuItem
                                    onClick={() =>
                                      statusMutation.mutate({
                                        id: album.id,
                                        status: "active",
                                      })
                                    }
                                    data-testid={`action-restore-${album.id}`}
                                  >
                                    <CheckCircle2 className="h-4 w-4 mr-2" />
                                    Restore
                                  </DropdownMenuItem>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>

                        <div className="flex items-center gap-1.5 flex-wrap">
                          <Badge
                            variant={
                              STATUS_CONFIG[album.status]?.variant ||
                              "secondary"
                            }
                            className="text-[10px] px-1.5 py-0"
                            data-testid={`badge-status-${album.id}`}
                          >
                            {STATUS_CONFIG[album.status]?.label || album.status}
                          </Badge>
                          <Badge
                            variant="outline"
                            className="text-[10px] px-1.5 py-0"
                          >
                            {getContentTypeIcon(album.albumType)}
                            <span className="ml-1 capitalize">
                              {album.albumType}
                            </span>
                          </Badge>
                          <Badge
                            variant="secondary"
                            className="text-[10px] px-1.5 py-0"
                          >
                            <ListMusic className="h-3 w-3 mr-1" />
                            {itemCount} {itemCount === 1 ? "item" : "items"}
                          </Badge>
                        </div>
                      </div>

                      <div className="flex items-center justify-between gap-2 mt-1.5">
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <span>{getCategoryName(album.categoryId)}</span>
                          <span className="text-muted-foreground/40">·</span>
                          <span>
                            {new Date(album.updatedAt).toLocaleDateString()}
                          </span>
                        </div>
                      </div>

                      {(album.tagOptionIds || []).length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-1.5">
                          {(album.tagOptionIds || []).slice(0, 4).map((tid) => {
                            const opt = tagOptions.find((o) => o.id === tid);
                            return opt ? (
                              <Badge
                                key={tid}
                                variant="outline"
                                className="text-[10px] font-normal px-1.5 py-0"
                              >
                                {opt.label}
                              </Badge>
                            ) : null;
                          })}
                          {(album.tagOptionIds || []).length > 4 && (
                            <Badge
                              variant="outline"
                              className="text-[10px] font-normal px-1.5 py-0"
                            >
                              +{(album.tagOptionIds || []).length - 4}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </Card>
              );
            })}
          </div>
          <PaginationControls
            currentPage={paginationProps.currentPage}
            totalPages={paginationProps.totalPages}
            totalItems={paginationProps.totalItems}
            startIndex={paginationProps.startIndex}
            endIndex={paginationProps.endIndex}
            canGoNext={paginationProps.canGoNext}
            canGoPrev={paginationProps.canGoPrev}
            onPageChange={paginationProps.setPage}
            onNext={paginationProps.nextPage}
            onPrev={paginationProps.prevPage}
          />
        </div>
      )}
    </div>
  );
}
